import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { get, map } from 'lodash';
import { CommonResponseList } from 'src/model/common';
import {
  BillingDetail,
  CampaignDetail,
  CustomerId,
  InvoiceDetail,
  InvoiceStripeDetail,
  ListInvoices,
  ListPlans,
  PlanDetail,
  PlanFeatures,
  TeamManageChartsSection,
  TeamMembers,
  UsageInformation,
  UserCreditUsages,
  UserSidebars
} from 'src/model/payment';
import { PaymentService } from 'src/services/api';
import { getRequest, postRequest } from 'src/services/api/http';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { URL_ENDPOINT } from 'src/utils/constants/service';
import { FieldRenameMapper } from 'src/utils/helpers/dataMappers/dataMappers';
import { IGetListInvoicesPayload, TypeCreditUsageLog } from 'src/utils/typings/dto/payment';
import { IInvoiceDetail } from 'src/utils/typings/response/payment';

const QUERY_KEY_CUSTOMER_ID = 'QUERY_KEY_CUSTOMER_ID';
const QUERY_KEY_BILLING_DETAIL = 'QUERY_KEY_BILLING_DETAIL';
const QUERY_KEY_LIST_PLANS = 'QUERY_KEY_LIST_PLANS';
const QUERY_KEY_LIST_INVOICES = 'QUERY_KEY_LIST_INVOICES';
const QUERY_KEY_INVOICE_STRIPE = 'QUERY_KEY_INVOICE_STRIPE';
export const QUERY_KEY_USAGE = 'QUERY_KEY_USAGE';
const QUERY_KEY_CREDIT_USER_USAGE_LOG = 'QUERY_KEY_CREDIT_USER_USAGE_LOG';
const QUERY_KEY_PLAN_FEATURES = 'QUERY_KEY_PLAN_FEATURES';
const QUERY_KEY_USER_CREDIT_USAGE = 'QUERY_KEY_USER_CREDIT_USAGE';
const QUERY_KEY_USER_SIDEBAR = 'QUERY_KEY_USER_SIDEBAR';
const QUERY_KEY_CAMPAIGN_DETAIL = 'QUERY_KEY_CAMPAIGN_DETAIL';

export const useQueryCustomerId = () => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_CUSTOMER_ID],
    queryFn: () => PaymentService.getCustomerId(),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CustomerId(),
    placeholderData: (previousData) => previousData,
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryBillingDetail = () => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_BILLING_DETAIL],
    queryFn: () => PaymentService.getBillingDetail(),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new BillingDetail(),
    placeholderData: (previousData) => previousData,
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryListPlans = (campaign_slug?: string) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_LIST_PLANS, campaign_slug],
    queryFn: () => PaymentService.getPlans(campaign_slug),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new ListPlans(),
    placeholderData: (previousData) => previousData,
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryPlanDetail = (id: number, campaign_slug?: string) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_LIST_PLANS, id],
    queryFn: () => PaymentService.getPlanDetail(id, campaign_slug),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PlanDetail(),
    placeholderData: (previousData) => previousData,
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryListInvoices = (query: IGetListInvoicesPayload) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_LIST_INVOICES],
    queryFn: () => PaymentService.getInvoices(query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new ListInvoices(),
    placeholderData: (previousData) => previousData,
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryInvoiceDetail = (id: string) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_LIST_INVOICES, id],
    queryFn: () => PaymentService.getInvoiceDetail(id),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new InvoiceDetail(),
    placeholderData: (previousData) => previousData,
    enabled: !!id,
  });

  const dataMapping: IInvoiceDetail = {
    id: get(data?.data, 'invoice.id', ''),
    payment_date: get(data?.data, 'history_invoice_data.payment_date', ''),
    status: get(data?.data, 'history_invoice_data.status', ''),
    payment_method: get(data?.data, 'history_invoice_data.payment_method', ''),
    customer_name: get(data?.data, 'invoice.customer_name', ''),
    customer_email: get(data?.data, 'invoice.customer_email', ''),
    customer_phone: get(data?.data, 'invoice.customer_phone', ''),
    customer_address: get(data?.data, 'invoice.customer_address', ''),
    plan: {
      name: get(data?.data, 'product.name', ''),
      recurring_interval: get(data?.data, 'invoice.lines.data[0].price.recurring.interval', ''),
      recurring_interval_count: get(data?.data, 'invoice.lines.data[0].price.recurring.interval_count', ''),
      amount: get(data?.data, 'history_invoice_data.amount', 0),
      // price: get(data?.data, 'invoice.total', 0),
      // quantity: get(data?.data, 'invoice.lines.total_count', 0),
    },
    period_end: dayjs(get(data?.data, 'invoice.lines.data[0].period.end', 0) * 1000).format(FORMAT_DATE_TIME.UTC),
  };

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data: dataMapping,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryInvoiceStripeBySessionId = (sessionId: string) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_INVOICE_STRIPE, sessionId],
    queryFn: () => PaymentService.getInvoiceStripeBySessionId(sessionId),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new InvoiceStripeDetail(),
    placeholderData: (previousData) => previousData,
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryUsageInformation = () => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_USAGE],
    queryFn: () => PaymentService.getUsage(),
    retry: 0,
    initialData: new UsageInformation(),
    placeholderData: (previousData) => previousData,
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};

export const useQueryCreditUsageLog = (body: Record<string, any>) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_CREDIT_USER_USAGE_LOG, body],
    queryFn: () => PaymentService.getCreditUsageLog(body),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CommonResponseList(),
    placeholderData: keepPreviousData,
  });
  return {
    data: {
      ...data,
      ...{
        data: map(data.data, (item) =>
          FieldRenameMapper.rename<any, TypeCreditUsageLog>(item, {
            feature_category: 'feature',
            exp_date: 'expired_at',
            identity: 'entity_name',
            activity: 'action',
            spent_date: 'created_at',
            spent: 'credits',
          }),
        ),
      },
    },
    isFetching,
  };
};

export const useQueryPlanFeatures = (params?: Record<string, string | number>) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_PLAN_FEATURES, params],
    queryFn: () => PaymentService.getPlanFeatures(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new PlanFeatures(),
    placeholderData: keepPreviousData,
  });
  return {
    data,
    isFetching,
  };
};
export const useQueryUserCreditUsage = (params: Record<string, any>) => {
  const { data, isFetching } = useQuery<UserCreditUsages>({
    queryKey: [QUERY_KEY_USER_CREDIT_USAGE, params],
    queryFn: () => PaymentService.getUserCreditUsage(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new UserCreditUsages(),
    placeholderData: keepPreviousData,
  });
  return {
    data,
    isFetching,
  };
};

export const useQueryUserSidebar = () => {
  return useQuery({
    queryFn: PaymentService.getUserSideBar,
    queryKey: [QUERY_KEY_USER_SIDEBAR],
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    initialData: new UserSidebars(),
  });
};
export const useQueryCampaignDetail = (params: Record<string, string | number>) => {
  const { isLoading, error, data, isError, isFetching, isLoadingError, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEY_CAMPAIGN_DETAIL, JSON.stringify(params)],
    queryFn: () => PaymentService.getCampaignProgram(params),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CampaignDetail(),
    placeholderData: (previousData) => previousData,
  });

  return {
    isLoadingError,
    isFetching,
    isLoading,
    error,
    data,
    isError,
    isPending,
    refetch,
  };
};


export const useQueryTeamMembers = (payload: Record<string, string | number>) => {
  return useQuery({
    queryKey: ['QUERY_KEY_TEAM_MEMBERS', payload],
    queryFn: () => postRequest(URL_ENDPOINT.payment.team.manage, payload) as Promise<TeamMembers>,
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TeamMembers(),
    placeholderData: keepPreviousData,
    enabled: !!payload.team_id,
  });
}

export const useQueryChartsSection = (query: Record<string, string | number>) => {
  return useQuery({
    queryKey: ['QUERY_KEY_CHARTS_SECTION', query],
    queryFn: () => getRequest(URL_ENDPOINT.payment.team.chartsSection, query) as Promise<TeamManageChartsSection>,
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new TeamManageChartsSection(),
    placeholderData: keepPreviousData,
    enabled: !!query.team_id,
  });
}
